import {useMemo} from "react";
import type {Order} from "../../../../domain/entities/Order";
import {checkoutCopy, checkoutLocale} from "../../../i18n/checkout/es";
import styles from "../../../pages/checkout/CheckoutPage.module.css";

const formatOrderDate = (date: Date) =>
    new Intl.DateTimeFormat(checkoutLocale, {
        year: "numeric",
        month: "short",
        day: "numeric",
    }).format(date);

const OrderSummaryHeader = ({code, createdAt}: { code: string; createdAt: Date }) => {
    const orderDate = useMemo(() => formatOrderDate(createdAt), [createdAt]);

    return (
        <header className={styles.orderHeader}>
            <h1 id="order-summary-title" className={styles.orderNumber}>
                {checkoutCopy.order.headingPrefix} {code}
            </h1>
            <span className={styles.orderDate} aria-label={checkoutCopy.order.dateLabel}>
                {orderDate}
            </span>
        </header>
    );
};

const OrderItemsList = ({items, formatter}: { items: Order["items"]; formatter: Intl.NumberFormat }) => (
    <div className={styles.orderItems}>
        {items.map((item) => (
            <div key={item.code} className={styles.orderItem}>
                <div className={styles.itemInfo}>
                    <span className={styles.itemName}>{item.name}</span>
                    {item.quantity && <small>Cantidad: {item.quantity}</small>}
                    {item.variantOptions && (
                        <small>{Object.entries(item.variantOptions).map(([key, value]) => `${key}: ${value}`).join(" · ")}</small>
                    )}
                </div>
                <span className={styles.itemPrice}>{formatter.format(item.price.amount)}</span>
            </div>
        ))}
    </div>
);

const OrderSummaryTotal = ({order, formatter}: { order: Order; formatter: Intl.NumberFormat }) => (
    <footer className={styles.orderSummary}>
        {order.pricing && order.pricing.discountMinor > 0 && (
            <>
                <div className={styles.summaryRow}>
                    <span>{checkoutCopy.order.originalTotal}</span>
                    <span>{formatter.format(order.pricing.grossTotalMinor / 100)}</span>
                </div>
                <div className={`${styles.summaryRow} ${styles.discountRow}`}>
                    <span>{checkoutCopy.order.discount}</span>
                    <span>-{formatter.format(order.pricing.discountMinor / 100)}</span>
                </div>
            </>
        )}
        <div className={`${styles.summaryRow} ${styles.summaryRowTotal}`}>
            <span>{checkoutCopy.order.total}</span>
            <span>{formatter.format(order.amount.amount)}</span>
        </div>
    </footer>
);

interface OrderSummarySectionProps {
    order: Order;
    couponCode: string;
    couponError: string | null;
    isCouponProcessing: boolean;
    onCouponChange: (code: string) => void;
    onApplyCoupon: () => Promise<void>;
    onRemoveCoupon: () => Promise<void>;
}

export const OrderSummarySection = ({
    order,
    couponCode,
    couponError,
    isCouponProcessing,
    onCouponChange,
    onApplyCoupon,
    onRemoveCoupon,
}: OrderSummarySectionProps) => {
    const currencyFormatter = useMemo(
        () =>
            new Intl.NumberFormat(checkoutLocale, {
                style: "currency",
                currency: order.amount.currency,
            }),
        [order.amount.currency],
    );

    return (
        <section className={styles.orderSection} aria-labelledby="order-summary-title">
            <OrderSummaryHeader code={order.code} createdAt={order.createdAt}/>
            <OrderItemsList items={order.items} formatter={currencyFormatter}/>
            <div className={styles.couponBox}>
                <label htmlFor="coupon-code" className={styles.couponLabel}>
                    {checkoutCopy.order.couponLabel}
                </label>
                <div className={styles.couponControls}>
                    <input
                        id="coupon-code"
                        className={styles.couponInput}
                        value={couponCode}
                        placeholder={checkoutCopy.order.couponPlaceholder}
                        onChange={(event) => onCouponChange(event.target.value)}
                        disabled={isCouponProcessing || Boolean(order.pricing?.couponCode)}
                        autoComplete="off"
                    />
                    {order.pricing?.couponCode ? (
                        <button className={styles.couponSecondaryButton} type="button" onClick={() => void onRemoveCoupon()} disabled={isCouponProcessing}>
                            {checkoutCopy.order.removeCoupon}
                        </button>
                    ) : (
                        <button className={styles.couponButton} type="button" onClick={() => void onApplyCoupon()} disabled={isCouponProcessing || !couponCode.trim()}>
                            {checkoutCopy.order.applyCoupon}
                        </button>
                    )}
                </div>
                {couponError && <p className={styles.couponError} role="alert">{couponError}</p>}
            </div>
            <OrderSummaryTotal order={order} formatter={currencyFormatter}/>
        </section>
    );
};
